import ManagedTable, { ManagedColumn, ManagedColumnExtra, ManagedTableProps } from 'app/common/table/ManagedTable';
import TimeUtils from 'app/common/util/TimeUtils';
import {
    CfmuFlightType,
    ExemptionReason,
    FamStatus,
    FlightId,
    RegulatedFlightDto,
    Slot,
    SlotForcedType,
    SlotStatus,
    SlotType,
    SlotUpdateKind,
} from 'app/model/CORE';
import React from 'react';
import Utils from 'app/common/util/Utils';
import useWindowDimensions from '../../common/hooks/useWindowDimensions';
import './SlotTable.less';
import { Flex } from '../common/Flex';
import { SlotMoreMenu } from '../dashboard/detailed/slots/SlotMoreMenu';

function toSlotTimeClassname(value: React.ReactNode, row: Slot): string {
    return row.outsideSlotWindow ? 'outsideSlotWindow' : null;
}

/* Slot Columns & Slot Table definitions */
export class SlotColumn extends ManagedColumn<Slot> {
    constructor(
        header: string,
        width: string,
        getCellValue: (object: Slot, parent: SlotTableBase) => any,
        extra?: ManagedColumnExtra<Slot>,
    ) {
        super(header, width, getCellValue, extra);
    }
}

export const SLOT_ACTIONS_COLUMNS = new SlotColumn(
    '',
    '4em',
    (slot, parent) => (
        <Flex className="more-button" onClick={e => e.stopPropagation()}>
            <SlotMoreMenu
                slot={slot}
                onBookSlot={parent.bookSlot}
                onUpdateSlot={parent.updateSlot}
                onFreeSlot={parent.freeSlot}
            />
        </Flex>
    ),
    { name: 'actions', className: () => 'slot-actions' },
);

export class StandardSlotColumn {
    public static readonly SLOT_TIME = new SlotColumn(
        'Time',
        '5em',
        object => TimeUtils.formatHHMM(object.referenceTime),
        { className: toSlotTimeClassname },
    );
    public static readonly SR = new SlotColumn(
        'SR',
        '4em',
        object =>
            toSlotStatusString(object.status) +
            (object.periodEnd ? toSlotTypeString(object.type).toLowerCase() : toSlotTypeString(object.type)),
    );
    public static readonly ARCID = new SlotColumn(
        'Aircraft',
        '7em',
        object => object.regulatedFlight?.flightId?.flightKeys?.callsign,
    );
    public static readonly ADEP = new SlotColumn(
        'ADEP',
        '5em',
        object => object.regulatedFlight?.flightId?.flightKeys?.adep,
    );
    public static readonly ADES = new SlotColumn(
        'ADES',
        '5em',
        object => object.regulatedFlight?.flightId?.flightKeys?.ades,
    );
    public static readonly KIND = new SlotColumn('Kind', '4em', object =>
        toFlightTypeString(object.regulatedFlight?.cfmuFlightType),
    );
    public static readonly RE_LF_XFR = new SlotColumn('RE Lf XFR', '7em', object =>
        toRELFXFRString(object.regulatedFlight),
    );
    public static readonly ETOT = new SlotColumn('ETOT', '5em', object =>
        TimeUtils.formatHHMM(object.regulatedFlight?.estimatedTakeOffTime),
    );
    public static readonly CTOT = new SlotColumn('CTOT', '5em', object => {
        const ctot = TimeUtils.formatHHMM(object.regulatedFlight?.calculatedTakeOffTime);
        if (ctot) return ctot + toForcedString(object.regulatedFlight);
    });
    public static readonly ATOT = new SlotColumn('ATOT', '5em', object =>
        TimeUtils.formatHHMM(object.regulatedFlight?.actualTakeOffTime),
    );
    public static readonly ETO = new SlotColumn('ETO', '5em', object =>
        TimeUtils.formatHHMM(object.regulatedFlight?.estimatedTimeOver),
    );
    public static readonly STO = new SlotColumn('STO', '5em', object =>
        TimeUtils.formatHHMM(object.regulatedFlight?.scheduledTimeOver),
    );
    public static readonly CTO = new SlotColumn('CTO', '5em', object => {
        const cto = TimeUtils.formatHHMM(object.regulatedFlight?.calculatedTimeOver);
        if (cto) return cto + toForcedString(object.regulatedFlight);
    });
    public static readonly ATO = new SlotColumn('ATO', '5em', object =>
        TimeUtils.formatHHMM(object.regulatedFlight?.actualTimeOver),
    );
    public static readonly DELAY = new SlotColumn('Delay', '5em', object => toDelayString(object.regulatedFlight));
    public static readonly REGS = new SlotColumn('Regs', '4em', object =>
        object.regulatedFlight?.regulationLocations?.length.toString(),
    );
    public static readonly LAST_MSG = new SlotColumn(
        'Last Msg',
        '6em',
        object => object.regulatedFlight?.lastAtfmMessageType,
    );
    public static readonly MPR = new SlotColumn(
        'REGUL+',
        '9em',
        object => object.regulatedFlight?.mostPenalisingRegulation,
    );
}

function toSlotStatusString(slotState: SlotStatus): string {
    switch (slotState) {
        case SlotStatus.AVAILABLE:
            return 'F';
        case SlotStatus.PRE_ALLOCATED:
            return 'P';
        case SlotStatus.ALLOCATED:
            return 'A';
    }
    return '';
}

function toSlotTypeString(slotType: SlotType): string {
    switch (slotType) {
        case SlotType.NORMAL:
            return 'N';
        case SlotType.OVERLOAD:
            return 'O'; // TODO: deal with period end slots
        case SlotType.PENDING:
            return 'P';
        // case SlotType.PERIOD_END:
        // 	return "o";
    }
    return '';
}

function toFlightTypeString(flightType: CfmuFlightType): string {
    switch (flightType) {
        case CfmuFlightType.ACT:
            return 'Act';
        case CfmuFlightType.IFPL:
            return 'Fpl';
        case CfmuFlightType.RPL:
            return 'Rpl';
        case CfmuFlightType.TACT_ACTIVATED:
            return 'Tac';
        case CfmuFlightType.TERMINATED:
            return 'Ter';
        case CfmuFlightType.MFD:
        case CfmuFlightType.PREDICTED_FLIGHT:
            return 'XXX';
    }
    return '';
}

function toRELFXFRString(regulatedFlight: RegulatedFlightDto): string {
    if (!regulatedFlight) return undefined;

    const re = regulatedFlight?.exempted ? toExemptionReasonString(regulatedFlight?.exemptionReason) : '.';
    const lf = regulatedFlight?.lateFiler ? 'F' : '.';
    const lu = +regulatedFlight?.lateUpdater ? 'U' : '.';
    const x = Utils.isNotEmpty(regulatedFlight?.excludedRegulationIds)
        ? regulatedFlight?.excludedRegulationIds.length
        : '.';
    const f = toFamStatusString(regulatedFlight?.famStatus);
    let r = '.';
    if (regulatedFlight?.readyStatus?.readyToDepart) {
        // Ready Status
        r = 'R';
    } else if (regulatedFlight?.readyStatus?.readyForImprovement) {
        r = 'I';
    }
    return re + ' ' + lf + ' ' + lu + ' ' + x + ' ' + f + ' ' + r;
}

function toExemptionReasonString(exemptionReason: ExemptionReason): string {
    if (exemptionReason) {
        switch (exemptionReason) {
            case ExemptionReason.ATFM_EXEMPTION_APPROVED:
                return '5';
            case ExemptionReason.EMERGENCY:
                return '2';
            case ExemptionReason.FIRE_FIGHTING:
                return '3';
            case ExemptionReason.HEAD_STATE:
                return '4';
            case ExemptionReason.LONG_HAUL:
                return 'Z';
            case ExemptionReason.MANUAL_EXEMPTION:
                return 'M';
            case ExemptionReason.MEDICAL_EVACUATION:
                return '1';
            case ExemptionReason.OTHER:
                return 'XXX';
            case ExemptionReason.SEARCH_AND_RESCUE:
                return '2';
        }
    }
    return '.';
}

function toFamStatusString(famStatus: FamStatus): string {
    if (famStatus) {
        switch (famStatus) {
            case FamStatus.NOT_UNDER_FAM:
                return '.';
            case FamStatus.SUBJECT_TO_FAM:
                return 'F';
            case FamStatus.WAS_SUBJECT_TO_FAM:
                return 'f';
            case FamStatus.SHIFTED_BY_FAM:
                return 'S';
            case FamStatus.WAS_SHIFTED_BY_FAM:
                return 's';
            case FamStatus.AIRBORNE_WHEN_SHIFTED_BY_FAM:
                return 'a';
            case FamStatus.SUSPENDED_BY_FAM:
                return 'U';
            case FamStatus.WAS_SUSPENDED_BY_FAM:
                return 'u';
            case FamStatus.AIRBORNE_WHEN_SUSPENDED_BY_FAM:
                return 'A';
        }
    }
    return '.';
}

function toForcedString(regulatedFlight: RegulatedFlightDto) {
    if (regulatedFlight?.forced) {
        switch (regulatedFlight.forced) {
            case SlotForcedType.FORCED_BY_FMD:
                return 'f';
            case SlotForcedType.FORCED_BY_TOWER:
                return 't';
            case SlotForcedType.FORCED_BY_CHAMAN:
                return 'c';
            case SlotForcedType.FORCED_BY_STAM:
                return 's';
            case SlotForcedType.FORCED_BY_FMD_FOR_SLOT_EXTENSION:
                return 'e';
            case SlotForcedType.FORCED_BY_ATC_DPI_FOR_SLOT_EXTENSION:
                return 'a';
        }
    } else if (regulatedFlight?.hasBeenForced) {
        return 'w';
    }
    return '';
}

function toDelayString(regulatedFlight: RegulatedFlightDto): string {
    if (Utils.isNotExists(regulatedFlight?.delayInMs) || regulatedFlight?.delayInMs < 0) return null;

    const delayInMin: number = regulatedFlight.delayInMs / TimeUtils.MIN_TO_MS;
    return regulatedFlight.mostPenalising ? delayInMin.toString() : '(' + delayInMin + ')';
}

class SlotTableBase extends ManagedTable<Slot, SlotTableProps> {
    bookSlot = (slot: Slot, flightId: FlightId) => this.props.onBookSlot(slot, flightId);
    updateSlot = (slot: Slot, updateKind: SlotUpdateKind) => this.props.onUpdateSlot(slot, updateKind);
    freeSlot = (slot: Slot) => this.props.onFreeSlot(slot);
}

export interface SlotTableProps extends ManagedTableProps<Slot> {
    onBookSlot?: (slot: Slot, flightId: FlightId) => void;
    onFreeSlot?: (slot: Slot) => void;
    onUpdateSlot?: (slot: Slot, updateKind: SlotUpdateKind) => void;
}

// I have had to adapt ManagedTable so that it accepts a font size.
// VirtualTable that is behind only accepts column width in "px", whereas our column definition is in "em".
// This usually is not a problem when font size is fixed, but in our case, font size depends on the viewport width.
// When the viewport is resized, the new font size is provided to the flight table, that will adapt column width accordingly.
const SlotTable = (props: SlotTableProps) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.42 * width) / 100;

    return <SlotTableBase fontSize={fontSize} {...props} />;
};

export default SlotTable;
